/* A station's running order as the show runner plans it: songs and DJ links back to back, about half an hour ahead.
   A link is planned as an empty slot and written about a song before it airs. Playout (Liquidsoap on the stream host) takes items one by one and reports when each starts; later times move
   with it. The stream itself: play.scai.fm/s/<station>/stream.mp3 (and index.m3u8 for HLS). */
import { For, Show, createResource, createSignal, onCleanup } from 'solid-js';
import { api, RadioLink, Station, StationItem } from '../api';
import { ErrorLine, useAction } from '../ui';

const STREAM = (import.meta.env.VITE_STREAM_BASE as string | undefined) ?? 'https://play.scai.fm';
const REASON = { link: 'Link', top_of_hour: 'Top of the hour', programme_start: 'Programme start', test: 'Try-out' };
const ms = (s: string) => new Date(s + 'Z').getTime();
const secs = (n: number) => `${Math.floor(n / 60000)}:${String(Math.round((n % 60000) / 1000)).padStart(2, '0')}`;

function LinkText(props: { link: RadioLink }) {
  return (
    <div class="flex flex-col gap-1">
      <span class="text-xs font-semibold uppercase tracking-wide text-magenta">{REASON[props.link.reason]}</span>
      <span class="text-sm">{props.link.text}</span>
      <Show when={props.link.audio_url} fallback={<span class="text-xs text-ink-3">Text only: the DJ has no voice yet, playout skips it.</span>}>
        <audio controls preload="none" class="h-8 w-full max-w-sm" src={props.link.audio_url!} />
      </Show>
    </div>
  );
}

export default function RunningOrder(props: { station: Station; onSaved: (s: Station) => void }) {
  const [items, { refetch }] = createResource(() => props.station.id + props.station.status,
    () => api<StationItem[]>(`/radio/stations/${props.station.id}/running-order`));
  const [tried, setTried] = createSignal<RadioLink | null>(null);
  const [now, setNow] = createSignal(Date.now());
  const a = useAction();
  const t = setInterval(() => { setNow(Date.now()); refetch(); }, 30_000);
  onCleanup(() => clearInterval(t));
  const onAir = () => props.station.status === 'on_air';
  const talks = () => props.station.programmes.some((p) => p.talk !== 'none');
  const time = (s: string) => new Date(s + 'Z').toLocaleTimeString('en-GB', { timeZone: props.station.timezone, hour: '2-digit', minute: '2-digit' });
  const playing = (i: StationItem) => ms(i.start_at) <= now() && now() < ms(i.start_at) + i.duration_ms;

  const setStatus = (status: string) => a.run(async () => {
    props.onSaved(await api<Station>(`/radio/stations/${props.station.id}`, { method: 'PATCH', json: { status } }));
  });
  const reset = () => a.run(async () => {
    await api(`/radio/stations/${props.station.id}/running-order/reset`, { method: 'POST' });
    refetch();
  });
  const tryLink = () => a.run(async () => setTried(await api<RadioLink>(`/radio/stations/${props.station.id}/try-link`, { method: 'POST' })));

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center gap-2">
        <Show when={onAir()} fallback={<button class="btn btn-sm btn-primary" disabled={a.busy()} onClick={() => setStatus('on_air')}>Start the show runner</button>}>
          <button class="btn btn-sm" disabled={a.busy()} onClick={() => setStatus('off')}>Stop the show runner</button>
          <button class="btn btn-sm btn-ghost" disabled={a.busy()} onClick={reset} title="After a grid or programme change">Plan afresh</button>
        </Show>
        <Show when={talks()}>
          <button class="btn btn-sm btn-ghost" disabled={a.busy()} onClick={tryLink}>{a.busy() ? 'Writing…' : 'Try a link'}</button>
        </Show>
      </div>
      <p class="text-sm text-ink-3">
        <Show when={onAir()} fallback={<>Off air: nothing is planned and the stream plays silence. Starting the show runner plans songs{props.station.dj
          ? ` and ${props.station.dj.display_name}'s links` : ''} about half an hour ahead.</>}>
          Songs are planned about half an hour ahead, links written about a song before they air; refreshed every minute,
          times follow playout once it reports.
        </Show>
      </p>
      <Show when={props.station.mount}>
        {(m) => (
          <div class="flex flex-wrap items-center gap-3">
            <audio controls preload="none" class="h-9 w-full max-w-xs" src={`${STREAM}${m()}/stream.mp3`} aria-label={`Listen to ${props.station.name}`} />
            <span class="mono text-xs text-ink-3">{STREAM}{m()}/stream.mp3 · {m()}/index.m3u8</span>
          </div>
        )}
      </Show>
      <ErrorLine error={a.error} />
      <Show when={tried()}>
        {(l) => <div class="rounded-box border border-line p-3"><LinkText link={l()} /></div>}
      </Show>
      <Show when={(items() ?? []).length}>
        <table class="table table-sm">
          <tbody>
            <For each={items()}>
              {(i) => (
                <tr class={`align-top ${i.status === 'aired' && !playing(i) ? 'opacity-50' : ''} ${playing(i) ? 'bg-static' : ''}`}>
                  <td class="mono w-16 whitespace-nowrap">{time(i.start_at)}</td>
                  <td class="w-full">
                    <Show when={i.link} fallback={i.kind === 'link'
                      ? <span class="text-xs text-ink-3"><span class="font-semibold uppercase tracking-wide text-magenta">{REASON[i.reason ?? 'link']}</span> · written about a song before it airs</span>
                      : i.kind === 'clip' ? <span><span class="text-xs font-semibold uppercase tracking-wide text-ink-3">Ident</span> {i.track?.title}</span>
                      : <span><b>{i.track?.title}</b> <span class="text-ink-2">{i.track?.artist}</span></span>}>
                      {(l) => <LinkText link={l()} />}
                    </Show>
                  </td>
                  <td class="whitespace-nowrap text-xs text-ink-3">{i.programme}</td>
                  <td class="mono whitespace-nowrap text-right text-xs text-ink-3">{playing(i) ? 'now' : secs(i.duration_ms)}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>
    </div>
  );
}
